import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Download,
  RefreshCw,
  Search,
  Loader2,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Bike,
  CalendarCheck,
  UtensilsCrossed,
  Filter,
} from "lucide-react";
import * as XLSX from "xlsx";

// Period shortcuts for quick date selection
const PERIOD_SHORTCUTS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

const PURPOSE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "commission_payment", label: "Bike Taxi Commission" },
  { value: "subscription_payment", label: "Subscription" },
  { value: "food_delivery_payment", label: "Food Delivery" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "paid", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "created", label: "Pending" },
];

export function RazorpayPaymentsReport({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  // Filters
  const [purpose, setPurpose] = useState("all");
  const [status, setStatus] = useState("all");
  const [driverSearch, setDriverSearch] = useState("");
  const [period, setPeriod] = useState("this_month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Calculate dates based on period
  const getDateRange = useCallback((periodValue) => {
    const today = new Date();
    let from, to;
    
    switch (periodValue) {
      case "today":
        from = new Date(today.setHours(0, 0, 0, 0));
        to = new Date();
        break;
      case "yesterday":
        from = new Date(today);
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(from);
        to.setHours(23, 59, 59, 999);
        break;
      case "this_week":
        from = new Date(today);
        from.setDate(from.getDate() - from.getDay());
        from.setHours(0, 0, 0, 0);
        to = new Date();
        break;
      case "last_week":
        from = new Date(today);
        from.setDate(from.getDate() - from.getDay() - 7);
        from.setHours(0, 0, 0, 0);
        to = new Date(from);
        to.setDate(to.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        break;
      case "this_month":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date();
        break;
      case "last_month":
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "custom":
        return { from: dateFrom, to: dateTo };
      default:
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date();
    }
    
    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0]
    };
  }, [dateFrom, dateTo]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange(period);
      const params = new URLSearchParams();
      
      if (purpose !== "all") params.append("purpose", purpose);
      if (status !== "all") params.append("status", status);
      if (driverSearch) params.append("driver_search", driverSearch);
      if (from) params.append("date_from", from);
      if (to) params.append("date_to", to);
      params.append("limit", limit.toString());
      params.append("offset", ((page - 1) * limit).toString());
      
      const response = await axios.get(`${API}/reports/razorpay-payments?${params}`);
      setPayments(response.data.payments);
      setTotal(response.data.total);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error("Failed to fetch payments");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [purpose, status, driverSearch, period, dateFrom, dateTo, page, limit, getDateRange]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (statusValue) => {
    switch (statusValue) {
      case "paid":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Success
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      case "created":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
            {statusValue || "Unknown"}
          </span>
        );
    }
  };

  const getPurposeIcon = (purposeValue) => {
    switch (purposeValue) {
      case "commission_payment":
        return <Bike className="w-4 h-4 text-blue-500" />;
      case "subscription_payment":
        return <CalendarCheck className="w-4 h-4 text-purple-500" />;
      case "food_delivery_payment":
        return <UtensilsCrossed className="w-4 h-4 text-orange-500" />;
      default:
        return <CreditCard className="w-4 h-4 text-gray-500" />;
    }
  };

  const exportToExcel = () => {
    const exportData = payments.map((p) => ({
      "Date & Time": formatDateTime(p.created_at),
      "Rider Name": p.driver_name,
      "Phone": p.driver_phone,
      "Payment Type": p.purpose_label,
      "Amount (₹)": p.amount,
      "Status": p.status_label,
      "Razorpay Order ID": p.razorpay_order_id,
      "Payment ID": p.razorpay_payment_id || "-",
      "Paid At": p.paid_at ? formatDateTime(p.paid_at) : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Razorpay Payments");
    XLSX.writeFile(wb, `razorpay_payments_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Report exported successfully");
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-amber-500" />
              All Rider Razorpay Payments
            </h2>
            <p className="text-sm text-muted-foreground">
              View all Razorpay payments made by riders
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPayments} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={exportToExcel} disabled={payments.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{summary.total_payments}</div>
              <p className="text-xs text-muted-foreground">Total Payments</p>
            </CardContent>
          </Card>
          <Card className="border-green-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{summary.paid_count}</div>
              <p className="text-xs text-muted-foreground">Success</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{summary.failed_count}</div>
              <p className="text-xs text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{summary.pending_count}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-700">₹{summary.total_amount_paid.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Total Collected</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Period */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Period</label>
              <Select value={period} onValueChange={(v) => { setPeriod(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_SHORTCUTS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {period === "custom" && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">From</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">To</label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  />
                </div>
              </>
            )}

            {/* Payment Type */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Payment Type</label>
              <Select value={purpose} onValueChange={(v) => { setPurpose(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rider Search */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Rider Name/Phone</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={driverSearch}
                  onChange={(e) => { setDriverSearch(e.target.value); setPage(1); }}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500">
              <CreditCard className="w-12 h-12 mb-4 text-gray-300" />
              <p>No payments found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Rider</TableHead>
                    <TableHead>Payment Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Razorpay ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(payment.created_at)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{payment.driver_name}</p>
                          {payment.driver_phone && (
                            <p className="text-xs text-muted-foreground">{payment.driver_phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getPurposeIcon(payment.purpose)}
                          <span>{payment.purpose_label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ₹{payment.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {payment.razorpay_order_id?.substring(0, 20)}...
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total} payments
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
